import React, { useEffect, useState } from "react";
import API from "../api.js";
import { Link } from "react-router-dom";
import { CATEGORIES } from "../config/constants.js";
import { APP_NAME } from "../config/theme.js";
import { MESSAGES } from "../config/messages.js";
import ImageSlider from "../components/ImageSlider.jsx";
import { toast } from "react-toastify";

function Home() {
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState("");


  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError("");

      try {
        const queryParams = new URLSearchParams();

        if (selectedCategory !== "All") {
          queryParams.append("category", selectedCategory);
        }

        if (searchTerm.trim()) {
          queryParams.append("search", searchTerm.trim());
        }

        const { data } = await API.get(
          `/products/all?${queryParams.toString()}`
        );

        setProducts(data.data);
      } catch (err) {
        setError("Failed to fetch products.");
        toast.error("Something went wrong.");
      } finally {
        setLoading(false);
        setIsInitialLoad(false);
      }
    };

    const debounce = setTimeout(fetchProducts, 500);
    return () => clearTimeout(debounce);
  }, [selectedCategory, searchTerm]);


  return (
    <div className="min-h-screen bg-slate-950 p-6 md:p-10">
      <div className="max-w-7xl mx-auto">

        <div className="mb-10">
          <h1 className="text-4xl font-extrabold text-white mb-2">
            Explore <span className="text-blue-500">{APP_NAME}</span>
          </h1>
          <p className="text-slate-500 font-medium">
            Find the best deals in your local bazar.
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-4 items-center bg-slate-900 p-4 rounded-3xl border border-slate-800 mb-12 shadow-2xl">

          <div className="relative w-full md:flex-1">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">
              🔍
            </span>

            <input
              type="text"
              placeholder="Search items (e.g. iPhone, Pulsar...)"
              className="w-full bg-slate-950 border border-slate-800 rounded-2xl pl-10 pr-4 py-3 text-white focus:border-blue-500 outline-none transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            className="w-full md:w-64 bg-slate-950 border border-slate-800 rounded-2xl px-4 py-3 text-white outline-none cursor-pointer focus:border-blue-500 transition-all"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        {isInitialLoad && loading ? (
          <div className="flex flex-col justify-center items-center h-96 text-center space-y-6">
            <div className="text-blue-500 animate-spin text-5xl">⚙️</div>

            <div className="max-w-md bg-slate-900 border border-blue-500/30 p-6 rounded-3xl shadow-2xl">
              <p className="text-slate-300 text-sm leading-relaxed">
                <span className="text-blue-500 font-bold block mb-2">
                  {MESSAGES.serverWake}
                </span>
                Our backend may sleep after inactivity.
              </p>
            </div>
          </div>
        ) : (
          <>
            {loading && (
              <div className="flex justify-center items-center py-20">
                <div className="text-blue-500 animate-spin text-4xl">
                  ⚙️
                </div>
              </div>
            )}

            {error && !loading && (
              <div className="text-center text-red-400 py-20">
                {error}
              </div>
            )}

            {!loading && !error && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                {products.length > 0 ? (
                  products.map((product) => (
                    <Link
                      to={`/product/${product._id}`}
                      key={product._id}
                      className="group"
                    >
                      <div className="bg-slate-900 border border-slate-800 rounded-[2rem] overflow-hidden hover:border-blue-500 duration-300 flex flex-col h-full shadow-lg">

                        <div className="relative w-full aspect-square overflow-hidden rounded-t-[2rem]">
                          <ImageSlider images={product.images}
                          className="w-full h-full" />

                          <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-md px-3 py-1 rounded-full text-[10px] text-white font-bold uppercase tracking-widest">
                            {product.category}
                          </div>
                        </div>

                        <div className="p-5 flex flex-col flex-1">
                          <h3 className="text-white font-bold text-lg truncate group-hover:text-blue-400 transition-colors">
                            {product.title}
                          </h3>

                          <div className="mt-auto pt-4 flex justify-between items-center">
                            <span className="text-2xl font-black text-white">
                              ₹{product.price?.toLocaleString("en-IN")}
                            </span>

                            <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-blue-500 group-hover:bg-blue-500 group-hover:text-white transition-all">
                              →
                            </div>
                          </div>
                        </div>

                      </div>
                    </Link>
                  ))
                ) : (
                  <div className="col-span-full text-center py-24 bg-slate-900/50 rounded-[3rem] border-2 border-dashed border-slate-800">
                    <p className="text-slate-500 text-xl font-medium">
                      No items found.
                    </p>

                    <button
                      onClick={() => {
                        setSearchTerm("");
                        setSelectedCategory("All");
                      }}
                      className="mt-4 text-blue-500 hover:underline"
                    >
                      Clear all filters
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Home;